import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const TERMS_VERSION = '1.0.0';

export default function ExternalTermsOfService() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBack();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-white">
      <div className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 active:scale-[0.99] transition-all"
            aria-label="חזרה"
          >
            <ArrowRight className="h-5 w-5" />
            חזרה
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-center">תנאי שימוש – LoyalFlow</h1>
        <p className="text-sm text-gray-500 mb-8 text-center">
          עודכן: {new Date().toLocaleDateString('he-IL')} | גרסה: {TERMS_VERSION}
        </p>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">1. כללי</h2>
            <p>
              ברוכים הבאים ל-LoyalFlow. השימוש במערכת מהווה הסכמה לתנאי שימוש אלה. אנא קראו אותם בעיון.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">2. השירות</h2>
            <p>
              LoyalFlow מספקת פלטפורמה לניהול עסקים, תורים, לקוחות ומערכות נאמנות. השירות ניתן "כמות שהוא" (AS-IS).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">3. הזמנת תורים</h2>
            <ul className="list-disc mr-6 space-y-2">
              <li>אתם אחראים לספק מידע מדויק בעת הזמנת תור</li>
              <li>יש להגיע בזמן לתור. איחור עשוי לגרום לביטול התור</li>
              <li>ביטול תור יש לבצע לפי מדיניות הביטול של העסק</li>
              <li>העסק רשאי לסרב לתת שירות במקרים מסוימים</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">4. שימוש מותר</h2>
            <p className="mb-2">השימוש במערכת מותר אך ורק למטרות חוקיות. אסור:</p>
            <ul className="list-disc mr-6 space-y-2">
              <li>להשתמש במערכת באופן שעלול לפגוע או להפריע לפעילותה</li>
              <li>לנסות לגשת למידע של משתמשים אחרים ללא הרשאה</li>
              <li>להעלות תוכן זדוני, פוגעני או בלתי חוקי</li>
              <li>לעשות שימוש במערכת לשליחת ספאם או הודעות לא רצויות</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">5. תשלומים</h2>
            <p>
              תשלומים עבור שירותים מתבצעים באמצעות ספקי תשלום מאובטחים. מחירי השירותים נקבעים
              על ידי העסק ועשויים להשתנות.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">6. הגבלת אחריות</h2>
            <p>
              LoyalFlow לא תהיה אחראית לנזקים ישירים או עקיפים הנובעים משימוש או אי-שימוש במערכת,
              לרבות אובדן נתונים, איחורים, או ביטולים.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">7. פרטיות</h2>
            <p>
              המידע האישי שלך מטופל בהתאם ל
              <a
                href="/book/privacy-policy"
                className="text-blue-600 hover:underline mx-1"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/book/privacy-policy', { state: { from: location.pathname } });
                }}
              >
                מדיניות הפרטיות
              </a>
              שלנו.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">8. שינויים בתנאי השימוש</h2>
            <p>
              אנו רשאים לעדכן את תנאי השימוש מעת לעת. שינויים יפורסמו באתר,
              והמשך השימוש במערכת לאחר השינוי מהווה הסכמה לתנאים החדשים.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">9. דין וסמכות שיפוט</h2>
            <p>
              תנאי שימוש אלה יפורשו על-פי חוקי מדינת ישראל.
              סמכות השיפוט הבלעדית תהיה לבתי המשפט המוסמכים בישראל.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">10. יצירת קשר</h2>
            <p>
              לשאלות או הבהרות בנוגע לתנאי השימוש, ניתן לפנות אלינו:{' '}
              <a href="mailto:support@loyalflow.co.il" className="text-blue-600 hover:underline">
                support@loyalflow.co.il
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
